/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColDef, ValueGetterParams } from '@ag-grid-community/core';
import { AgFns } from './ag-fns';
import { EntityStateRendererComponent } from './entity-state-renderer.component';
//import * as _ from "lodash";
import { ActiveStatusEnum, Product } from '@models';
import { Entity } from 'breeze-client';
import { IEntityWithStatus, StatusService } from '../../services/status.service';
import { AgButtonProps } from './button-renderer.component';

export class ProxAgFns {
  static getEntityStateColDef(getEntityFn?: (item: any) => Entity, hide = false): ColDef {
    const cellRendererParams: any = {};
    if (getEntityFn) {
      cellRendererParams.getEntityFn = getEntityFn;
    }
    return {
      headerName: '',
      sortable: false,
      cellRenderer: EntityStateRendererComponent,
      cellRendererParams: cellRendererParams,
      ...AgFns.fixWidth(60),
      hide: hide,
      suppressHeaderMenuButton: true,
      colId: '_entityState',
    };
  }

  static getSupplierColDef(field: string): ColDef {
    return {
      headerName: 'Supplier',
      field: field,
      filter: 'agTextColumnFilter',
      ...AgFns.flexWidth(120),
    };
  }

  static getManufacturerColDef(field: string): ColDef {
    return {
      headerName: 'Manufacturer',
      field: field,
      filter: 'agTextColumnFilter',
      ...AgFns.flexWidth(120),
    };
  }

  static getProductTypeConfigColDef(field: string): ColDef {
    return {
      headerName: 'Config / Catalog',
      field: field,
      filter: 'agTextColumnFilter',
      ...AgFns.flexWidth(110),
    };
  }

  static getProductTypeNameColDef(field: string): ColDef {
    return {
      headerName: 'Product Type Name',
      field: field,
      filter: 'agTextColumnFilter',
      ...AgFns.flexWidth(150),
    };
  }

  static getProductTypeDescriptionColDef(field: string): ColDef {
    return {
      headerName: 'Product Type Description',
      field: field,
      filter: 'agTextColumnFilter',
      ...AgFns.flexWidth(150),
    };
  }

  static getProductNameColDef(field: string): ColDef {
    return {
      headerName: 'Product Name',
      field: field,
      filter: 'agTextColumnFilter',
      ...AgFns.flexWidth(120),
    };
  }

  static getActiveStatusColDef(field: string) {
    // Derive the sibling activeStatusId path from the display-name path so that
    // 'proximityUser.activeStatus.name' -> 'proximityUser.activeStatusId' and
    // 'activeStatus.name' -> 'activeStatusId'. Callers that pass a field NOT ending
    // in '.activeStatus.name' or literally 'activeStatus.name' get no color styling —
    // just the plain Status text.
    const statusIdPath = field.replace(/(^|\.)activeStatus\.name$/, '$1activeStatusId');
    return {
      headerName: 'Status',
      field: field,
      cellClass: (p) => {
        const statusId = ProxAgFns.getNestedValue(p.data, statusIdPath);
        if (statusId === ActiveStatusEnum.Hold) return 'qb-status-hold';
        if (statusId === ActiveStatusEnum.Inactive) return 'qb-status-inactive';
        return '';
      },
      ...AgFns.fixWidth(100),
    } as ColDef;
  }

  /** Walk a dotted path on an object. Returns undefined at the first null hop. */
  private static getNestedValue(obj: any, path: string): any {
    if (obj == null || !path) return undefined;
    const parts = path.split('.');
    let cur: any = obj;
    for (const part of parts) {
      if (cur == null) return undefined;
      cur = cur[part];
    }
    return cur;
  }

  static getEntityDeleteColDef<T>(deleteMethodBound: (arg: T) => void | Promise<void> | Promise<boolean>, buttonProps?: AgButtonProps) {
    return {
      ...AgFns.createButtonProps('', deleteMethodBound, {
        icon: 'pi pi-trash',
        ...buttonProps,
      }),
      ...AgFns.fixWidth(60),
      suppressHeaderMenuButton: true,
      colId: 'deleteBtn',
    } as ColDef;
  }

  static getEntityStatusColDef<T>(statusMethodBound: (arg: T) => void | Promise<void>, buttonProps?: AgButtonProps) {
    return {
      ...AgFns.createButtonProps('', statusMethodBound, {
        colId: 'status',
        icon: 'fa-regular fa-key',
        tooltip: '',
        ...buttonProps,
      }),
      ...AgFns.fixWidth(60),
      suppressHeaderMenuButton: true,
    } as ColDef;
  }

  static getWorkingStatusDef(
    headerName: string,
    statusService: StatusService,
    entityFn: (p: ValueGetterParams) => IEntityWithStatus = (p) => p.data,
  ) {
    return {
      headerName: headerName,
      valueGetter: (params: ValueGetterParams) => {
        return statusService.getWorkingStatus(entityFn(params)).shortDisplay;
      },
      ...AgFns.fixWidth(100),
      sortable: false,
    } as ColDef;
  }

  static getProductSortOrderColDef(productFn: (p: ValueGetterParams) => Product, colId = 'productSortOrder', shouldHide = true) {
    return {
      colId: colId,
      headerName: 'Product Sort Order', // if not hidden
      hide: shouldHide,
      valueGetter: (params) => {
        const product = productFn(params);
        return product?.getSortOrder();
      },
    } as ColDef;
  }

  /** Move moveItem in the array before or after targetItem, and renumber the displayOrder values.
   * If targetItem is null, move moveItem to the end of the array.
   * Assumes items are already sorted by displayOrder. */
  static moveItemInDisplayOrder<T>(items: T[], fieldName: string, moveItem: T, targetItem?: T, isAfter = false) {
    // set initial numbers if none exist
    if (items.every((i) => i[fieldName] === 0)) {
      this.renumberSortOrder(items, fieldName);
    }

    if (!targetItem) {
      // add moveItem to the end
      moveItem[fieldName] = items[items.length - 1][fieldName] + 1;
    } else {
      // move to just before targetItem.  Use backingStore to prevent rounding to int.
      const offset = isAfter ? 0.5 : -0.5;
      moveItem['_backingStore'][fieldName] = targetItem[fieldName] + offset;
    }
    items.sort((a, b) => a[fieldName] - b[fieldName]);
    this.renumberSortOrder(items, fieldName);
  }

  /** renumber the displayOrder of the rows, based on current sort */
  static renumberSortOrder<T>(items: T[], fieldName: string) {
    let i = 1;

    for (const me of items) {
      if (me[fieldName] !== i) {
        me[fieldName] = i;
      }
      i++;
    }
  }

  //// Old code for dragging and dropping rows to set display order column
  //// No longer used because it was too tedious to use for large lists. - Now we just edit the display order.

  // static getDisplayOrderColumnDef<T>(gridOptions: GridOptionsExt, getItems: () => T[], fieldName: string, dragFieldPath: string): ColDef {
  //   if (!gridOptions.onRowDragEnd) {
  //     gridOptions.onRowDragEnd = (event: RowDragEvent) => {
  //       const items = getItems();
  //       this.sortDisplayOrderAfterDrag(items, fieldName, event);
  //     };
  //     gridOptions.rowDragText = (params) => {
  //       return _.get(params.rowNode?.data, dragFieldPath) ?? 'item';
  //     };
  //   }
  //   const onShift = (itemToMove: T, event?: CellClickedEvent, origEvent?) => {
  //     const items = getItems();
  //     this.moveItemUpOrDown(items, fieldName, itemToMove, event, origEvent);
  //   }
  //   return {
  //     field: fieldName, minWidth: 50, maxWidth: 50, ...AgFns.createIconProps('', 'pi pi-sort', onShift,
  //       { label: '', icon: 'pi pi-sort py-2', rowDrag: true },  ), sortable: false
  //     };
  // }

  // /** Move the item up or down in the display order */
  // private static moveItemUpOrDown<T>(items: T[], fieldName: string, moveItem: T, event?: CellClickedEvent, origEvent?: UIEvent) {
  //   // move down if click was in lower half of icon
  //   const y = (origEvent as PointerEvent).offsetY;
  //   const isDown = y > ((origEvent as any).target.offsetHeight / 2);

  //   const pos = items.findIndex(x => x === moveItem);
  //   if (isDown) {
  //     if (pos < 0 || pos > items.length - 2) {
  //       return;
  //     }
  //     this.moveItemInDisplayOrder(items, fieldName, moveItem, items[pos + 2]);
  //   } else {
  //     if (pos <= 0) {
  //       return;
  //     }
  //     this.moveItemInDisplayOrder(items, fieldName, moveItem, items[pos - 1]);
  //   }

  //   AgFns.refreshGrid(event?.context.gridOptions, items as object[]);
  //   event?.api.clearFocusedCell();
  // }

  /** Set the display order and sort the rows after a drag-drop move.
   * Assumes items are already in display order.
   */
  // private static sortDisplayOrderAfterDrag<T>(items: T[], fieldName: string, event: RowDragEvent) {
  //   event.api.refreshCells();
  //   const movingItem = event.node.data as T;
  //   const overItem = event.overNode?.data as T;

  //   // move after the overItem if the drop event was in the lower half of the row
  //   const isAfter = event.overNode && event.y - (event.overNode.rowTop || 0) > 15;

  //   this.moveItemInDisplayOrder(items, fieldName, movingItem, overItem, isAfter);

  //   // tell the grid to refresh
  //   AgFns.refreshGrid(event?.context.gridOptions, items as object[]);
  //   event?.api.clearFocusedCell();
  // }
}
